import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Avatar,
  Chip,
  Button
} from '@mui/material';
import {
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Tune as TuneIcon,
  Email as EmailIcon,
  Public as PublicIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

/**
 * Componente de tarjeta reutilizable para mostrar empresas/negocios
 *
 * @param {Object} empresa - Objeto con datos de la empresa
 * @param {String} tipo - 'pendiente' | 'activa'
 * @param {Function} onAprobar - Callback al hacer click en Aprobar (solo pendientes)
 * @param {Function} onConfigurar - Callback al hacer click en Configurar (solo activas)
 */
function TarjetaEmpresa({ empresa, tipo, onAprobar, onConfigurar }) {

  const esPendiente = tipo === 'pendiente';
  const esActiva = tipo === 'activa';

  return (
    <Card
      elevation={3}
      sx={{
        borderRadius: 3,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Header con logo y estado */}
        <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: esPendiente ? '#FFA726' : '#66BB6A',
              boxShadow: esPendiente
                ? '0 4px 12px rgba(255, 167, 38, 0.3)'
                : '0 4px 12px rgba(102, 187, 106, 0.3)'
            }}
          >
            <BusinessIcon fontSize="large" />
          </Avatar>

          <Chip
            label={esPendiente ? 'Pendiente' : 'Activa'}
            color={esPendiente ? 'warning' : 'success'}
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        </Box>

        {/* Nombre de la empresa */}
        <Typography
          variant="h6"
          fontWeight="bold"
          gutterBottom
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: 60
          }}
        >
          {empresa.nombre}
        </Typography>

        {/* Datos básicos */}
        <Box display="flex" flexDirection="column" gap={1.5} mt={2}>
          {/* RUC */}
          <Box display="flex" alignItems="center" gap={1}>
            <BusinessIcon fontSize="small" sx={{ color: '#78909C' }} />
            <Typography variant="body2" color="textSecondary">
              RUC: <strong>{empresa.ruc}</strong>
            </Typography>
          </Box>

          {/* País */}
          <Box display="flex" alignItems="center" gap={1}>
            <PublicIcon fontSize="small" sx={{ color: '#78909C' }} />
            <Typography variant="body2" color="textSecondary">
              {empresa.nombre_pais || 'País no especificado'}
            </Typography>
          </Box>

          {/* Email */}
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon fontSize="small" sx={{ color: '#78909C' }} />
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {empresa.correo_contacto}
            </Typography>
          </Box>

          {/* Plantilla aplicada (solo activas) */}
          {esActiva && empresa.nombre_plantilla && (
            <Box display="flex" alignItems="center" gap={1}>
              <AssignmentIcon fontSize="small" sx={{ color: '#1A237E' }} />
              <Typography variant="body2" color="textSecondary">
                Plantilla: <strong>{empresa.nombre_plantilla}</strong>
              </Typography>
            </Box>
          )}

          {/* Sin plantilla (solo activas) */}
          {esActiva && !empresa.nombre_plantilla && (
            <Box display="flex" alignItems="center" gap={1}>
              <AssignmentIcon fontSize="small" sx={{ color: '#FFA726' }} />
              <Typography variant="body2" sx={{ color: '#FFA726' }}>
                Sin plantilla aplicada
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>

      {/* Acciones */}
      <CardActions sx={{ p: 2, pt: 0 }}>
        {esPendiente && (
          <Button
            fullWidth
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => onAprobar && onAprobar(empresa)}
            sx={{
              py: 1.5,
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #66BB6A 0%, #4CAF50 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                boxShadow: '0 4px 12px rgba(102, 187, 106, 0.4)'
              }
            }}
          >
            Aprobar
          </Button>
        )}

        {esActiva && (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={<TuneIcon />}
            onClick={() => onConfigurar && onConfigurar(empresa)}
            sx={{
              py: 1.5,
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                boxShadow: '0 4px 12px rgba(26, 35, 126, 0.4)'
              }
            }}
          >
            Configurar
          </Button>
        )}
      </CardActions>
    </Card>
  );
}

export default TarjetaEmpresa;
